import { css } from "glamor";
import React from "react";
import { IsMobileDevice } from "source/app/domain/Device/Helpers/IsMobile";
import { StyleCache } from "./StyleCache/StyleCache";

export type TCSS = React.CSSProperties;

export type TExtendedCSS = Partial<
    React.CSSProperties & { ":before": React.CSSProperties } & {
        ":after": React.CSSProperties;
    } & {
        ":hover": React.CSSProperties;
    } & { ":checked": React.CSSProperties } & {
        ":focus": React.CSSProperties;
    } & { ":active": React.CSSProperties }
>;

export function Style(label: string, CSS: ICSSWithIndexer) {
    const cacheKey = PrepareCSSAndGetCacheKey(label, CSS);
    const cachedStyle = StyleCache.Lookup(cacheKey);

    if (cachedStyle) {
        return cachedStyle;
    }

    const cssStyle = css({ ...CSS, label });
    StyleCache.Add(cacheKey, cssStyle);

    return cssStyle;
}

export function StyleClass(label: string, CSS: ICSSWithIndexer) {
    const cacheKey = PrepareCSSAndGetCacheKey(label, CSS);
    const cachedStyle = StyleCache.Lookup(cacheKey);

    if (cachedStyle) {
        return `${cachedStyle}`;
    }

    const cssStyle = css({ ...CSS, label });
    StyleCache.Add(cacheKey, cssStyle);

    return `${cssStyle}`;
}

function PrepareCSSAndGetCacheKey(label: string, CSS: ICSSWithIndexer) {
    if (IsMobileDevice()) {
        RemoveAnimationProperties(CSS);
    }

    return `${label}_${GetSimpleHash(JSON.stringify(CSS))}`;
}

export function RemoveAnimationProperties(CSS: ICSSWithIndexer) {
    for (var property in CSS) {
        if (Object.prototype.hasOwnProperty.call(CSS, property)) {
            const HandleNestedProperties = () => {
                const isPseudoClassStyle = typeof CSS[property] === "object";

                if (isPseudoClassStyle) {
                    RemoveAnimationProperties(CSS[property]);
                }
            };

            HandleNestedProperties();

            if (
                property.includes("animation") ||
                property.includes("transition")
            ) {
                delete CSS[property];
            }
        }
    }

    if (CSS.opacity === 0) {
        CSS.opacity = 1;
    }
}

export interface ICSSWithIndexer extends TExtendedCSS {
    [x: string]: any;
}

// This is a simple, *insecure* hash that's short, fast, and has no dependencies.
// For algorithmic use, where security isn't needed, it's way simpler than sha1 (and all its deps)
// or similar, and with a short, clean (base 36 alphanumeric) result.
// Loosely based on the Java version; see
// https://stackoverflow.com/questions/6122571/simple-non-secure-hash-function-for-javascript
export const GetSimpleHash = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash &= hash; // Convert to 32bit integer
    }
    return new Uint32Array([hash])[0].toString(36);
};
